import React from 'react';
import {
  Typography,
  Box,
  Grid,
  Button,
  useTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Star as StarIcon,
  Verified as VerifiedIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { PageLayout } from '../../components/footer/PageLayout';
import { ContentSection, FeatureCard } from '../../components/footer/ContentSection';
import { CTASection } from '../../components/footer/CTASection';

export const FindCaregiversPage: React.FC = () => {
  const theme = useTheme();

  const features = [
    {
      icon: <SearchIcon sx={{ fontSize: 40 }} />,
      title: "Advanced Search",
      description: "Find caregivers by specialty, location, availability, and ratings. Use our powerful filters to narrow down your options.",
      action: <Button variant="outlined" href="/auth">Start Searching</Button>,
    },
    {
      icon: <VerifiedIcon sx={{ fontSize: 40 }} />,
      title: "Verified Professionals",
      description: "All caregivers are background-checked, licensed, and verified. We ensure only qualified professionals join our platform.",
      action: <Button variant="outlined" href="/safety">Learn About Safety</Button>,
    },
    {
      icon: <StarIcon sx={{ fontSize: 40 }} />,
      title: "Real Reviews",
      description: "Read authentic reviews from other patients. See ratings, detailed feedback, and success stories to make informed decisions.",
      action: <Button variant="outlined" href="/stories">Read Stories</Button>,
    },
    {
      icon: <ScheduleIcon sx={{ fontSize: 40 }} />,
      title: "Flexible Scheduling",
      description: "Book appointments that fit your schedule. Many caregivers offer same-day, evening, and weekend availability.",
      action: <Button variant="outlined" href="/how-it-works">How It Works</Button>,
    },
    {
      icon: <LocationIcon sx={{ fontSize: 40 }} />,
      title: "Convenient Locations",
      description: "Choose between in-home care, clinic visits, or virtual sessions. Find caregivers who serve your area.",
      action: <Button variant="outlined" href="/auth">Find Local Care</Button>,
    },
    {
      icon: <FilterIcon sx={{ fontSize: 40 }} />,
      title: "Specialized Services",
      description: "Access physical therapy, occupational therapy, senior care, and home health services from qualified professionals.",
      action: <Button variant="outlined" href="/services">View Services</Button>,
    },
  ];

  const steps = [
    {
      step: "1",
      title: "Search & Filter",
      description: "Enter your location and service needs. Use our filters to find caregivers who match your specific requirements.",
    },
    {
      step: "2",
      title: "Review Profiles",
      description: "Browse detailed caregiver profiles including qualifications, experience, reviews, and availability.",
    },
    {
      step: "3",
      title: "Book Appointment",
      description: "Schedule your appointment directly through our platform. Choose a time that works for both you and your caregiver.",
    },
    {
      step: "4",
      title: "Receive Care",
      description: "Meet your caregiver and receive quality care in the comfort of your home or at a convenient location.",
    },
  ];

  return (
    <PageLayout
      title="Find Qualified Caregivers"
      subtitle="Connect with verified, experienced caregivers for physical therapy, occupational therapy, senior care, and home health services."
      breadcrumbs={[
        { label: "Find Caregivers" },
      ]}
    >
      {/* Hero Section */}
      <ContentSection
        title="Your Health, Your Choice"
        subtitle="Take control of your healthcare journey with our comprehensive network of qualified caregivers. Find the right professional for your specific needs, when and where you need them."
        backgroundColor={theme.palette.grey[50]}
      >
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={6} lg={4} key={index}>
              <FeatureCard {...feature} />
            </Grid>
          ))}
        </Grid>
      </ContentSection>

      {/* How It Works */}
      <ContentSection
        title="How to Find Your Perfect Caregiver"
        subtitle="Our simple 4-step process makes it easy to connect with qualified healthcare professionals."
      >
        <Grid container spacing={4}>
          {steps.map((step, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Box sx={{ textAlign: 'center' }}>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    backgroundColor: theme.palette.primary.main,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    mx: 'auto',
                    mb: 2,
                  }}
                >
                  {step.step}
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 'bold',
                    mb: 1,
                    color: theme.palette.text.primary,
                  }}
                >
                  {step.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    lineHeight: 1.6,
                  }}
                >
                  {step.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </ContentSection>

      {/* Search Tips */}
      <ContentSection
        title="Search Tips for Better Results"
        subtitle="Get the most out of your caregiver search with these helpful tips."
        backgroundColor={theme.palette.grey[50]}
      >
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                Use Specific Keywords
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
                Be specific about your needs. Instead of "therapy," try "physical therapy for back pain" or "occupational therapy for stroke recovery."
              </Typography>
              
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                Check Availability
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
                Look for caregivers who are available during your preferred times. Many offer flexible scheduling including evenings and weekends.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                Read Reviews Carefully
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
                Pay attention to reviews from patients with similar conditions or needs. Look for patterns in feedback about communication and results.
              </Typography>
              
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                Consider Location
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
                Choose caregivers who serve your area or offer virtual sessions. Consider travel time and convenience when making your selection.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </ContentSection>

      {/* CTA Section */}
      <CTASection
        title="Ready to Find Your Caregiver?"
        subtitle="Join thousands of patients who have found quality care through our platform. Start your search today."
        primaryButton={{
          text: "Get Started",
          href: "/auth",
        }}
        secondaryButton={{
          text: "Learn More",
          href: "/how-it-works",
          variant: "outlined",
        }}
      />
    </PageLayout>
  );
};
